import React, { useCallback, useState } from 'react';

import { OfferItem } from '@occc-app/routes/CampaignComponents/components/OfferSelect/types';
import { useChainId } from '@occc-app/Chain/hooks';
import {
  Earning_Mechanism_Typename,
  OfferFilter,
  Publication_Status_Type,
  useLoyaltyEventCreateMutation,
} from '@occc/graphql';
import { snackbar } from '@occc-app/design-system';
import { useGetOfferByIdLazy } from '@occc-app/dataAccessLayer/offer/queries';
import { earningMechanismHelper } from '@occc-app/offerHelpers/utils/earningMechanismHelper';
import { useIsUserPermittedTo, Feature } from '@occc/utils-access-control';

import { wait } from '../../../../../utils/utils';

const REFETCH_DELAY_MS = 1200;

export const useExecuteLoyaltyEvents = (customerUuid: string, onExecuted?: () => void) => {
  const chainId = useChainId();
  const isUserPermittedToExecuteLoyaltyEvent = useIsUserPermittedTo(Feature.MEMBER_EDIT);

  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loyaltyEventName, setLoyaltyEventName] = useState<string>();
  const [loyaltyEventType, setLoyaltyEventType] = useState<string>();

  const makeLoyaltyEventFilter = React.useCallback(
    (search: string): OfferFilter => {
      return {
        chainIdEquals: chainId,
        search: search || undefined,
        earningMechanismTypenameIncludes: [Earning_Mechanism_Typename.EarningMechanismLoyaltyEvent],
        deleted: false,
        publicationStatusEquals: Publication_Status_Type.Published,
        visibleForCommandCenterFulfillment: true,
      };
    },
    [chainId],
  );

  const closeConfirmDialog = () => {
    setIsConfirmDialogOpen(false);
  };

  const [fetchLoyaltyEvent] = useGetOfferByIdLazy(chainId);

  const [createLoyaltyEventMutation] = useLoyaltyEventCreateMutation({
    refetchQueries: ['membership', 'membershipActivities'],
    onCompleted: () => {
      setLoading(false);
      closeConfirmDialog();
      snackbar.success(`${loyaltyEventName} was executed`);
      if (onExecuted) {
        onExecuted();
      }
    },
    onError: () => {
      snackbar.error(`${loyaltyEventName} was not completed`);
      setLoading(false);
    },
    onQueryUpdated: async (observable) => {
      await wait(REFETCH_DELAY_MS); // on the BE side all linked updates require a some time, let's wait
      return observable.refetch();
    },
    awaitRefetchQueries: true,
  });

  const submit = useCallback(
    async ({ loyaltyEvent }: { loyaltyEvent: OfferItem }) => {
      setLoading(true);
      setIsConfirmDialogOpen(true);
      const { data } = await fetchLoyaltyEvent(loyaltyEvent.id);
      if (data?.earningMechanism && earningMechanismHelper.isLoyaltyEvent(data.earningMechanism)) {
        setLoyaltyEventName(data.name);
        setLoyaltyEventType(data.earningMechanism.loyaltyEventEventType);
      } else {
        closeConfirmDialog();
        snackbar.error('Not a Loyalty Event completed');
      }
      setLoading(false);
    },
    [fetchLoyaltyEvent],
  );

  const execute = React.useCallback(async () => {
    if (!loyaltyEventType) {
      return;
    }
    setLoading(true);
    await createLoyaltyEventMutation({
      variables: {
        input: {
          chainId,
          eventType: loyaltyEventType,
          customerUuid,
        },
      },
    });
  }, [loyaltyEventType, chainId, customerUuid, createLoyaltyEventMutation]);

  return {
    isConfirmDialogOpen,
    closeConfirmDialog,
    makeLoyaltyEventFilter,
    loyaltyEventName,
    loyaltyEventType,
    submit,
    execute,
    loading,
    isUserPermittedToExecuteLoyaltyEvent,
  };
};
